import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FamilyTreeCanvas from '../components/familyTree/FamilyTreeCanvas';
import { Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import useMemberSurveillanceOverview from '../hooks/useMemberSurveillanceOverview';
import { statusNarrative } from '../utils/intakeStatusNarrative';
import {
  buildFamilyTreePreviewFromIntake,
  canPatientEditIntake,
  createEmptyAllergy,
  createEmptyDiagnosis,
  createEmptyTimelineEntry,
  createEmptyGeneticTest,
  createEmptyMedication,
  createEmptyRelative,
  ensurePersonalHistory,
  getFamilyHistoryChangeRequests,
  getFamilyHistoryIntake,
  saveFamilyHistoryIntakeDraft,
  submitFamilyHistoryIntake,
  createFamilyHistoryChangeRequest,
} from '../services/familyHistoryIntakeService';
import {
  ANCESTRY_CATALOG,
  FamilyHistoryChangeRequestRecord,
  IntakeAncestryEntry,
  IntakeAncestryLineage,
  IntakeAncestryRegion,
  IntakeConditionCode,
  IntakeConditionResponse,
  IntakeFamilyConditionAssignment,
  IntakeFamilyConditionRelativeDetail,
  IntakeMedicationDoseUnit,
  IntakeMedicationFrequencyPeriod,
  IntakeDrinkFrequency,
  IntakeDrinksPerSession,
  IntakeDrugUse,
  IntakeHeavySessionFrequency,
  IntakeMedicalTimelineEntry,
  IntakePersonalAllergy,
  IntakePersonalDiagnosis,
  IntakePersonalHistory,
  IntakePersonalMedication,
  IntakePriorGeneticTest,
  IntakeRelativeRecord,
  PatientFamilyHistoryIntakeRecord,
} from '../types/familyHistoryIntake';
import {
  CONDITION_CATALOG,
  CONDITION_CODES,
  RELATION_CATALOG,
  RELATION_CODES,
  type IntakeRelation,
} from '../types/intakeVocabulary';
import { computeIntakeReadiness, type IntakeReadinessVerdict } from '../types/intakeReadiness';
import {
  ActionChip,
  BodyIcon,
  ChevronRightIcon,
  HistoryIcon,
  PageHeader,
  PersonIcon,
  PulseIcon,
  SectionCard,
  TileGrid,
} from '../components/layout/memberPrimitives';

/* =====================================================================
 * Screen model
 * ===================================================================== */

type ScreenId =
  | 'overview'
  | 'welcome'
  | 'basics-name'
  | 'basics-dob-sex'
  | 'contact'
  | 'medications'
  | 'allergies'
  | 'diagnoses'
  | 'surgeries'
  | 'genetic-tests'
  | 'ancestry'
  | 'family-structure'
  | 'family-tree'
  | 'conditions-heart'
  | 'conditions-cancer'
  | 'conditions-other'
  | 'lifestyle'
  | 'review';

type ScreenCategory = 'intro' | 'health' | 'family' | 'conditions' | 'context' | 'review';

interface ScreenDef {
  id: ScreenId;
  category: ScreenCategory;
  categoryLabel: string;
  showSkip: boolean;
}

const SCREENS: ScreenDef[] = [
  { id: 'welcome', category: 'intro', categoryLabel: 'Intro', showSkip: false },
  { id: 'basics-name', category: 'intro', categoryLabel: 'Intro', showSkip: false },
  { id: 'basics-dob-sex', category: 'intro', categoryLabel: 'Intro', showSkip: false },
  { id: 'contact', category: 'intro', categoryLabel: 'Intro', showSkip: true },
  { id: 'medications', category: 'health', categoryLabel: 'Your health', showSkip: true },
  { id: 'allergies', category: 'health', categoryLabel: 'Your health', showSkip: true },
  { id: 'diagnoses', category: 'health', categoryLabel: 'Your health', showSkip: true },
  { id: 'surgeries', category: 'health', categoryLabel: 'Your health', showSkip: true },
  { id: 'genetic-tests', category: 'health', categoryLabel: 'Your health', showSkip: true },
  { id: 'ancestry', category: 'health', categoryLabel: 'Your health', showSkip: true },
  { id: 'family-structure', category: 'family', categoryLabel: 'Your family', showSkip: false },
  { id: 'family-tree', category: 'family', categoryLabel: 'Your family', showSkip: false },
  { id: 'conditions-heart', category: 'conditions', categoryLabel: 'Conditions', showSkip: false },
  { id: 'conditions-cancer', category: 'conditions', categoryLabel: 'Conditions', showSkip: false },
  { id: 'conditions-other', category: 'conditions', categoryLabel: 'Conditions', showSkip: false },
  { id: 'lifestyle', category: 'context', categoryLabel: 'Lifestyle', showSkip: true },
  { id: 'review', category: 'review', categoryLabel: 'Review', showSkip: false },
];

const CONDITION_GROUPS: Record<'heart' | 'cancer' | 'other', IntakeConditionCode[]> = {
  heart: ['MI', 'STROKE', 'SUDDEN_CARDIAC_DEATH', 'HYPERLIPIDAEMIA', 'HYPERTENSION'],
  cancer: ['BREAST', 'OVARIAN', 'COLORECTAL', 'PROSTATE', 'PANCREATIC', 'MELANOMA', 'ENDOMETRIAL', 'OTHER_CANCER'],
  other: ['T2D', 'T1D', 'DEMENTIA', 'PARKINSON', 'VTE', 'LUPUS', 'COELIAC'],
};

const CONDITION_GROUP_COPY: Record<'heart' | 'cancer' | 'other', { title: string; subtitle: string; none: string }> = {
  heart: {
    title: 'Heart disease and stroke',
    subtitle: 'Any of these — in yourself or a blood relative?',
    none: 'None of these in me or my family',
  },
  cancer: {
    title: 'Cancer',
    subtitle: 'Any of these cancers in yourself or a blood relative?',
    none: 'No cancer in me or my family',
  },
  other: {
    title: 'Other conditions',
    subtitle: 'Anything else relevant for your family history?',
    none: 'None of these in me or my family',
  },
};

const RELATION_OPTIONS = RELATION_CODES.map((code) => ({ code, meta: RELATION_CATALOG[code] }));
const CONDITION_CODE_OPTIONS = CONDITION_CODES.map((code) => ({ code, meta: CONDITION_CATALOG[code] }));

/* =====================================================================
 * Primitives
 * ===================================================================== */

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>
      {children}
    </label>
  );
}

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border px-4 py-3 text-sm ${props.className || ''}`}
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)', ...(props.style || {}) }}
    />
  );
}

/**
 * Auto-growing textarea.
 *
 * This used to hard-code `className` and drop `props.style` entirely, so no
 * call site could size a single box — every long clinical answer got the same
 * three rows however much the member typed, and you could not read back what
 * you had written without scrolling inside a four-line window.
 *
 * It now forwards both, and grows to fit its content. `rows` still sets the
 * floor, so a field declared `rows={4}` never collapses smaller than that.
 */
function TextArea({ className, style, onChange, ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Reset first. Without this the height only ever ratchets upwards, because
    // scrollHeight can never report less than the element's current height.
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Re-measure on external value changes too — rehydrating a draft, or a
  // relative card switching to a different person — not only on keystrokes.
  useLayoutEffect(fit, [fit, props.value]);

  return (
    <textarea
      {...props}
      ref={ref}
      onChange={(event) => {
        fit();
        onChange?.(event);
      }}
      className={`w-full rounded-xl border px-4 py-3 text-sm ${className || ''}`}
      style={{
        borderColor: 'var(--melio-border)',
        backgroundColor: 'var(--b-bone)',
        color: 'var(--melio-charcoal)',
        // The box sizes itself, so the drag handle would only fight it.
        resize: 'none',
        overflow: 'hidden',
        ...(style || {}),
      }}
    />
  );
}

function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className="w-full rounded-xl border px-4 py-3 text-sm"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
    />
  );
}

function RemoveRowButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-2 rounded-lg text-sm"
      style={{ color: '#8f4d4d', backgroundColor: 'rgba(175, 82, 82, 0.1)' }}
    >
      Remove
    </button>
  );
}

function PrimaryButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={`px-5 py-3 rounded text-sm font-medium disabled:opacity-50 ${props.className || ''}`}
      style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)', ...(props.style || {}) }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={`px-5 py-3 rounded text-sm font-medium border disabled:opacity-50 ${props.className || ''}`}
      style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)', background: 'transparent', ...(props.style || {}) }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={`text-sm underline ${props.className || ''}`}
      style={{ color: 'var(--melio-text-muted)', background: 'transparent', ...(props.style || {}) }}
    >
      {children}
    </button>
  );
}

/* =====================================================================
 * Utility functions
 * ===================================================================== */

function getRelativeDisplayLabel(relative: IntakeRelativeRecord) {
  if (relative.relation === 'other' && relative.relationOtherDetails?.trim()) {
    return relative.relationOtherDetails.trim();
  }
  const meta = RELATION_CATALOG[relative.relation];
  if (meta) return meta.label;
  return String(relative.relation).replace(/_/g, ' ');
}

function mergeConditionIntoRelative(
  relative: IntakeRelativeRecord,
  conditionLabel: string,
  notes: string,
  code: IntakeConditionCode | undefined,
  ageAtDiagnosis: number | null | undefined,
) {
  const matchKey = code
    ? (condition: typeof relative.conditions[number]) => condition.code === code
    : (condition: typeof relative.conditions[number]) => condition.label === conditionLabel;
  const existing = relative.conditions.find(matchKey);
  if (existing) {
    return {
      ...relative,
      conditions: relative.conditions.map((condition) =>
        matchKey(condition)
          ? { ...condition, code: code ?? condition.code, label: conditionLabel, notes, ageAtDiagnosis: ageAtDiagnosis ?? condition.ageAtDiagnosis ?? null }
          : condition,
      ),
    };
  }

  return {
    ...relative,
    conditions: [
      ...relative.conditions,
      { id: crypto.randomUUID(), code, label: conditionLabel, notes, ageAtDiagnosis: ageAtDiagnosis ?? null },
    ],
  };
}

/**
 * Rebuild each relative's conditions from the condition cards the member ticked.
 *
 * The relatives array is derived, not authored: the member answers "who in your
 * family has had this?" on the condition screens, and this projects those
 * answers onto the people.
 *
 * IT MUST NOT DESTROY WHAT A CLINICIAN RECORDED. This used to reset every
 * relative to `conditions: []` and rebuild purely from `conditionAssignments`,
 * which meant that after a Care Clinician captured a family history on the call
 * — writing back through projectInterviewRelativesToIntake — the member's very
 * next autosave silently deleted all of it. There is no condition card for
 * "bowel polyps at 44 on the maternal side"; the clinician's finding simply had
 * nowhere to survive.
 *
 * So a condition is preserved when it carries a controlled `code` that no
 * condition card accounts for. That is the signature of clinician capture: the
 * card path always has an assignment backing it, and free-text conditions with
 * no code stay disposable.
 */
function syncRelativeConditionsFromAssignments(intake: PatientFamilyHistoryIntakeRecord): PatientFamilyHistoryIntakeRecord {
  // Every code the member's own condition cards can account for.
  const codesOwnedByCards = new Set(
    intake.conditionAssignments
      .filter((assignment) => assignment.response === 'family' || assignment.response === 'both')
      .map((assignment) => assignment.code)
      .filter(Boolean),
  );

  const baseRelatives: IntakeRelativeRecord[] = intake.relatives.map((relative) => ({
    ...relative,
    conditions: (relative.conditions || []).filter(
      (condition) => condition.code && !codesOwnedByCards.has(condition.code),
    ),
  }));

  const nextRelatives = intake.conditionAssignments.reduce((relatives, assignment) => {
    if (assignment.response === 'family' || assignment.response === 'both') {
      return relatives.map((relative) => {
        if (!assignment.familyMemberIds.includes(relative.id)) return relative;
        const detail = assignment.relativeDetails?.find((entry) => entry.relativeId === relative.id);
        return mergeConditionIntoRelative(
          relative,
          assignment.conditionLabel,
          detail?.notes || assignment.familyNotes,
          assignment.code,
          detail?.ageAtDiagnosis ?? null,
        );
      });
    }
    return relatives;
  }, baseRelatives);

  return { ...intake, relatives: nextRelatives };
}

function autoLineageForRelation(relation: IntakeRelation): IntakeRelativeRecord['lineageSide'] | null {
  const meta = RELATION_CATALOG[relation];
  if (!meta) return null;
  return meta.lineage;
}

function autoSexForRelation(relation: IntakeRelation, fallback: IntakeRelativeRecord['sex']): IntakeRelativeRecord['sex'] {
  const label = RELATION_CATALOG[relation]?.label?.toLowerCase?.() || '';
  if (/mother|sister|aunt|daughter|grandmother/.test(label)) return 'female';
  if (/father|brother|uncle|son|grandfather/.test(label)) return 'male';
  return fallback;
}

function formatStatusLabel(status: PatientFamilyHistoryIntakeRecord['status']) {
  switch (status) {
    case 'draft': return 'Draft';
    case 'submitted': return 'Submitted';
    case 'changes_requested': return 'Changes requested';
    case 'care_clinician_review':
    case 'doctor_in_review': return 'Clinician reviewing';
    case 'medical_director_signoff':
    case 'ops_signoff_pending': return 'Awaiting sign-off';
    case 'released':
    case 'signed_off': return 'Released';
    default: return status;
  }
}

/**
 * What a member sees once their intake is locked.
 *
 * Replaces a one-line "Editing is paused — your intake is in submitted."
 * That sentence had three problems. It read as an error rather than the
 * successful end of a task; it interpolated the raw status into the middle of a
 * sentence, so it literally said "is in submitted"; and it offered nowhere to
 * go. A member who had just finished the longest form in the product was left
 * on a screen still headed "Before you submit", with the submit button gone,
 * no confirmation, and the only exits two 12px muted links at the bottom of the
 * page.
 *
 * So this says what state the history is in, what happens next, and gives a
 * real way out. Finishing something should feel finished.
 */
/**
 * Shown when a clinician has sent the history back for changes.
 *
 * `changes_requested` re-opens the form for editing, so without this the member
 * gets an editable page and no indication anything happened — the clinician's
 * note was written to the intake but rendered nowhere on this side. This is the
 * only place they are told why.
 */
function ChangesRequestedNotice({ message }: { message?: string }) {
  return (
    <div
      className="rounded-2xl border p-5 mb-6"
      style={{ borderColor: 'var(--melio-oxblood, #5C1A1C)', backgroundColor: 'var(--melio-cream-warm)' }}
    >
      <p
        className="text-sm font-medium mb-2"
        style={{ fontFamily: 'var(--melio-display)', color: 'var(--melio-charcoal)' }}
      >
        Your clinician has asked for a few changes.
      </p>
      {message ? (
        <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--melio-text)' }}>
          &ldquo;{message}&rdquo;
        </p>
      ) : (
        <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--melio-text-muted)' }}>
          They did not leave a note. If it is not clear what to change, message your
          care team and they will explain.
        </p>
      )}
      <p className="text-xs leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
        Your history is editable again. Make the changes and submit it once more —
        nothing else you entered has been lost.
      </p>
    </div>
  );
}

function IntakeLockedNotice({
  status,
  showBackToHistory = false,
}: {
  status: PatientFamilyHistoryIntakeRecord['status'];
  /** Shown inside the step-by-step view, where /history is a level up. */
  showBackToHistory?: boolean;
}) {
  const { title, body } = statusNarrative(status);

  return (
    <div
      className="rounded-2xl border p-5 mb-6"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
    >
      <p
        className="text-lg mb-2"
        style={{ fontFamily: 'var(--melio-display)', color: 'var(--melio-ink)' }}
      >
        {title}
      </p>
      <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)', lineHeight: 1.6 }}>
        {body}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {/* The primary action on a finished form is leaving it. */}
        <Link
          to="/home"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
          style={{ backgroundColor: 'var(--melio-ink)', color: 'var(--melio-cream-warm)' }}
        >
          Back to your dashboard
          <ChevronRightIcon size={14} />
        </Link>
        {showBackToHistory && (
          <Link
            to="/history"
            className="inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-medium"
            style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-ink)' }}
          >
            Back to your history
          </Link>
        )}
        <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
          Reading is fine — editing is paused while it is with the practice.
        </span>
      </div>
    </div>
  );
}

/* =====================================================================
 * Condition card — tri-state toggle (None / Me / Family)
 * ===================================================================== */

type TriResponse = 'none' | 'me' | 'family';

function toTriResponse(response: IntakeConditionResponse): TriResponse {
  if (response === 'self' || response === 'both') return 'me';
  if (response === 'family') return 'family';
  return 'none';
}

function ConditionCard({
  assignment,
  relatives,
  editable,
  onChange,
}: {
  assignment: IntakeFamilyConditionAssignment;
  relatives: IntakeRelativeRecord[];
  editable: boolean;
  onChange: (next: IntakeFamilyConditionAssignment) => void;
}) {
  const tri = toTriResponse(assignment.response);
  const alsoInFamily = assignment.response === 'both';
  const bloodRelatives = relatives.filter((relative) => relative.bloodRelated);

  const setTri = (next: TriResponse) => {
    if (!editable) return;
    if (next === 'none') {
      onChange({ ...assignment, response: 'neither', familyMemberIds: [], relativeDetails: [] });
    } else if (next === 'me') {
      onChange({ ...assignment, response: 'self' });
    } else {
      onChange({ ...assignment, response: 'family' });
    }
  };

  const toggleAlsoInFamily = (checked: boolean) => {
    if (!editable) return;
    if (checked) onChange({ ...assignment, response: 'both' });
    else onChange({ ...assignment, response: 'self', familyMemberIds: [], relativeDetails: [] });
  };

  const toggleRelative = (relativeId: string, checked: boolean) => {
    const next = checked
      ? [...assignment.familyMemberIds, relativeId]
      : assignment.familyMemberIds.filter((id) => id !== relativeId);
    const nextDetails = (assignment.relativeDetails || []).filter((detail) => detail.relativeId !== relativeId || checked);
    if (checked && !nextDetails.find((detail) => detail.relativeId === relativeId)) {
      nextDetails.push({ relativeId, ageAtDiagnosis: null, notes: '' });
    }
    onChange({ ...assignment, familyMemberIds: next, relativeDetails: nextDetails });
  };

  const setRelativeDetail = (relativeId: string, patch: Partial<IntakeFamilyConditionRelativeDetail>) => {
    const nextDetails = (assignment.relativeDetails || []).slice();
    const idx = nextDetails.findIndex((detail) => detail.relativeId === relativeId);
    if (idx >= 0) nextDetails[idx] = { ...nextDetails[idx], ...patch };
    else nextDetails.push({ relativeId, ageAtDiagnosis: null, notes: '', ...patch });
    onChange({ ...assignment, relativeDetails: nextDetails });
  };

  const familyPickerOpen = tri === 'family' || alsoInFamily;

  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{assignment.conditionLabel}</p>
        <Segmented
          options={[
            { value: 'none', label: 'None' },
            { value: 'me', label: 'Me' },
            { value: 'family', label: 'Family' },
          ]}
          value={tri}
          disabled={!editable}
          onChange={(value) => setTri(value as TriResponse)}
        />
      </div>

      {tri === 'me' && (
        <label className="flex items-center gap-2 text-sm mt-3" style={{ color: 'var(--melio-text-muted)' }}>
          <input
            type="checkbox"
            disabled={!editable}
            checked={alsoInFamily}
            onChange={(event) => toggleAlsoInFamily(event.target.checked)}
          />
          <span>Also in my blood family?</span>
        </label>
      )}

      {familyPickerOpen && (
        <div className="mt-3">
          {bloodRelatives.length === 0 ? (
            <p className="text-sm" style={{ color: '#8f4d4d' }}>
              Add blood relatives on the family step first so you can point to who this applies to.
            </p>
          ) : (
            <div className="space-y-2">
              {bloodRelatives.map((relative) => {
                const checked = assignment.familyMemberIds.includes(relative.id);
                const detail = assignment.relativeDetails?.find((entry) => entry.relativeId === relative.id);
                return (
                  <div key={relative.id} className="rounded-xl border px-3 py-2" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={!editable}
                        checked={checked}
                        onChange={(event) => toggleRelative(relative.id, event.target.checked)}
                      />
                      <span>{getRelativeDisplayLabel(relative)}</span>
                    </label>
                    {checked && (
                      <div className="grid md:grid-cols-[140px_1fr] gap-3 mt-2">
                        <TextInput
                          type="number"
                          min={0}
                          step={1}
                          placeholder="Age at dx"
                          disabled={!editable}
                          value={detail?.ageAtDiagnosis ?? ''}
                          onChange={(event) => setRelativeDetail(relative.id, { ageAtDiagnosis: event.target.value ? Math.max(0, Math.floor(Number(event.target.value))) : null })}
                        />
                        <TextArea
                          rows={2}
                          placeholder="Notes (optional)"
                          disabled={!editable}
                          value={detail?.notes || ''}
                          onChange={(event) => setRelativeDetail(relative.id, { notes: event.target.value })}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Segmented({
  options,
  value,
  onChange,
  disabled,
}: {
  options: Array<{ value: string; label: string }>;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div
      className="inline-flex rounded-xl border overflow-hidden"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className="px-4 py-2 text-sm disabled:opacity-50"
            style={{
              background: active ? 'var(--melio-charcoal)' : 'transparent',
              color: active ? 'var(--melio-cream)' : 'var(--melio-charcoal)',
              borderRight: '1px solid var(--melio-border)',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* =====================================================================
 * Progressive-disclosure pedigree row
 * ===================================================================== */

function RelativeRow({
  relative,
  editable,
  onChange,
  onRemove,
}: {
  relative: IntakeRelativeRecord;
  editable: boolean;
  onChange: (next: IntakeRelativeRecord) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const setField = <K extends keyof IntakeRelativeRecord>(key: K, value: IntakeRelativeRecord[K]) => {
    onChange({ ...relative, [key]: value });
  };

  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
      <div className="grid md:grid-cols-[1fr_160px_120px_auto] gap-3">
        <div>
          <FieldLabel>Relationship</FieldLabel>
          <Select
            value={relative.relation}
            disabled={!editable}
            onChange={(event) => {
              const nextRelation = event.target.value as IntakeRelation;
              const autoLineage = autoLineageForRelation(nextRelation);
              onChange({
                ...relative,
                relation: nextRelation,
                lineageSide: autoLineage || relative.lineageSide,
                sex: autoSexForRelation(nextRelation, relative.sex),
              });
            }}
          >
            <optgroup label="Immediate family">
              {RELATION_OPTIONS.filter(({ meta }) => meta.degree === 1).map(({ code, meta }) => (
                <option key={code} value={code}>{meta.label}</option>
              ))}
            </optgroup>
            <optgroup label="Maternal side">
              {RELATION_OPTIONS.filter(({ meta }) => meta.lineage === 'maternal' && meta.degree > 1).map(({ code, meta }) => (
                <option key={code} value={code}>{meta.label}</option>
              ))}
            </optgroup>
            <optgroup label="Paternal side">
              {RELATION_OPTIONS.filter(({ meta }) => meta.lineage === 'paternal' && meta.degree > 1).map(({ code, meta }) => (
                <option key={code} value={code}>{meta.label}</option>
              ))}
            </optgroup>
            <option value="other">Other / unspecified</option>
          </Select>
        </div>
        <div>
          <FieldLabel>Status</FieldLabel>
          <Select
            value={relative.aliveStatus}
            disabled={!editable}
            onChange={(event) => setField('aliveStatus', event.target.value as IntakeRelativeRecord['aliveStatus'])}
          >
            <option value="alive">Alive</option>
            <option value="deceased">Deceased</option>
            <option value="unknown">Unknown</option>
          </Select>
        </div>
        <div>
          <FieldLabel>{relative.aliveStatus === 'deceased' ? 'Age at death' : 'Current age'}</FieldLabel>
          <TextInput
            type="number"
            min={0}
            step={1}
            disabled={!editable}
            value={relative.aliveStatus === 'deceased' ? (relative.ageAtDeath ?? '') : (relative.currentAge ?? '')}
            onChange={(event) => {
              const raw = event.target.value ? Math.max(0, Math.floor(Number(event.target.value))) : null;
              if (relative.aliveStatus === 'deceased') setField('ageAtDeath', raw);
              else setField('currentAge', raw);
            }}
          />
        </div>
        {editable && (
          <div className="flex items-end">
            <RemoveRowButton onClick={onRemove} />
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-4">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="text-sm underline"
          style={{ color: 'var(--melio-text-muted)', background: 'transparent' }}
        >
          {expanded ? 'Hide extra details' : 'Add extra details'}
        </button>
        {relative.relation === 'other' && (
          <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
            (Describe "Other" below)
          </span>
        )}
      </div>

      {expanded && (
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          <div>
            <FieldLabel>Sex</FieldLabel>
            <Select value={relative.sex} disabled={!editable} onChange={(event) => setField('sex', event.target.value as IntakeRelativeRecord['sex'])}>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="unknown">Unknown</option>
            </Select>
          </div>
          <div>
            <FieldLabel>Blood-related?</FieldLabel>
            <Select value={relative.bloodRelated ? 'yes' : 'no'} disabled={!editable} onChange={(event) => setField('bloodRelated', event.target.value === 'yes')}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </div>
          {relative.aliveStatus === 'deceased' && (
            <div className="md:col-span-2">
              <FieldLabel>Cause of death</FieldLabel>
              <TextArea
                rows={2}
                disabled={!editable}
                value={relative.causeOfDeath || ''}
                placeholder="As much as you know — the cause, their age, anything you were told"
                onChange={(event) => setField('causeOfDeath', event.target.value)}
              />
            </div>
          )}
          <div>
            <FieldLabel>Prior genetic testing</FieldLabel>
            <Select
              value={relative.priorGeneticTesting}
              disabled={!editable}
              onChange={(event) => setField('priorGeneticTesting', event.target.value as IntakeRelativeRecord['priorGeneticTesting'])}
            >
              <option value="unknown">Unknown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </Select>
          </div>
          {relative.relation === 'other' && (
            <div>
              <FieldLabel>Relationship detail</FieldLabel>
              <TextArea
                rows={2}
                disabled={!editable}
                value={relative.relationOtherDetails || ''}
                onChange={(event) => setField('relationOtherDetails', event.target.value)}
                placeholder="Describe without using a name"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <FieldLabel>Notes</FieldLabel>
            <TextArea
              rows={2}
              disabled={!editable}
              value={relative.notes || ''}
              onChange={(event) => setField('notes', event.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* =====================================================================
 * Editable list helper
 * ===================================================================== */

function EditableList<T extends { id: string }>(props: {
  title: string;
  subtitle?: string;
  addLabel: string;
  editable: boolean;
  onAdd: () => void;
  items: T[];
  empty: string;
  renderItem: (item: T, index: number, remove: (index: number) => void) => React.ReactNode;
  onRemoveAt?: (index: number) => void;
}) {
  const { title, subtitle, addLabel, editable, onAdd, items, empty, renderItem, onRemoveAt } = props;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{title}</p>
          {subtitle && <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{subtitle}</p>}
        </div>
        {editable && (
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg text-sm"
            style={{ backgroundColor: 'rgba(84, 112, 146, 0.12)', color: 'var(--melio-charcoal)' }}
            onClick={onAdd}
          >
            {addLabel}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{empty}</p>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => renderItem(item, index, onRemoveAt || (() => {})))}
        </div>
      )}
    </div>
  );
}

function ConditionCodeSelect({
  value,
  label,
  disabled,
  onChange,
}: {
  value?: IntakeConditionCode;
  label: string;
  disabled: boolean;
  onChange: (code: IntakeConditionCode | undefined, label: string) => void;
}) {
  return (
    <Select
      value={value || ''}
      disabled={disabled}
      onChange={(event) => {
        const nextCode = event.target.value ? (event.target.value as IntakeConditionCode) : undefined;
        const nextLabel = nextCode ? CONDITION_CATALOG[nextCode].label : label;
        onChange(nextCode, nextLabel);
      }}
    >
      <option value="">Choose condition…</option>
      {CONDITION_CODE_OPTIONS.map(({ code, meta }) => (
        <option key={code} value={code}>{meta.label}</option>
      ))}
    </Select>
  );
}

/* =====================================================================
 * Screen shell + chrome
 * ===================================================================== */

function ScreenShell({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-3xl border p-6 md:p-10"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
    >
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
          {eyebrow}
        </p>
      )}
      <h2
        className="text-2xl md:text-3xl font-normal mb-2"
        style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="text-sm md:text-base mb-6" style={{ color: 'var(--melio-text-muted)' }}>
          {subtitle}
        </p>
      )}
      <div>{children}</div>
    </div>
  );
}

function HeaderBar({
  status,
  progress,
  categoryLabel,
  currentIndex,
  total,
  onShowTree,
  onShowHelp,
  saveState,
}: {
  status: PatientFamilyHistoryIntakeRecord['status'];
  progress: number;
  categoryLabel: string;
  currentIndex: number;
  total: number;
  onShowTree: () => void;
  onShowHelp: () => void;
  saveState: 'idle' | 'saving' | 'saved';
}) {
  return (
    <div className="mb-5">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="text-xs uppercase tracking-widest"
            style={{ color: 'var(--melio-sage)' }}
          >
            Family history
          </span>
          <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>·</span>
          <span className="text-xs font-medium truncate" style={{ color: 'var(--melio-charcoal)' }}>
            {categoryLabel}
          </span>
          <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>·</span>
          <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
            Step {currentIndex + 1} of {total}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
            {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : ''}
          </span>
          <button
            type="button"
            onClick={onShowTree}
            className="text-xs underline"
            style={{ color: 'var(--melio-text-muted)', background: 'transparent' }}
          >
            View family tree
          </button>
          <button
            type="button"
            onClick={onShowHelp}
            className="inline-flex items-center justify-center rounded-full border"
            style={{
              width: 22,
              height: 22,
              borderColor: 'var(--melio-border)',
              color: 'var(--melio-text-muted)',
              background: 'white',
              fontSize: 12,
              fontWeight: 500,
            }}
            aria-label="Help"
            title="What happens with my answers?"
          >
            i
          </button>
          <span
            className="text-xs px-2 py-1 rounded-full"
            style={{ backgroundColor: 'rgba(79, 93, 24, 0.12)', color: 'var(--melio-sage)' }}
          >
            {formatStatusLabel(status)}
          </span>
        </div>
      </div>
      <div className="h-1 rounded-full" style={{ backgroundColor: 'rgba(79, 93, 24, 0.15)' }}>
        <div
          className="h-1 rounded-full transition-all"
          style={{ width: `${Math.max(4, Math.min(100, progress * 100))}%`, backgroundColor: 'var(--melio-charcoal)' }}
        />
      </div>
    </div>
  );
}

function HelpPopover({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(48, 46, 40, 0.4)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 max-w-md"
        style={{ backgroundColor: 'var(--melio-cream, white)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--melio-sage)' }}>
          What happens with my answers
        </p>
        <h3 className="text-xl mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
          You submit · clinician reviews · director signs off · released to your plan.
        </h3>
        <ol className="space-y-2 text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
          <li><strong style={{ color: 'var(--melio-charcoal)' }}>1.</strong> You finish this form. Your draft saves as you go.</li>
          <li><strong style={{ color: 'var(--melio-charcoal)' }}>2.</strong> Your senior care clinician structures your pedigree and flags anything that needs following up.</li>
          <li><strong style={{ color: 'var(--melio-charcoal)' }}>3.</strong> A medical director signs it off so it can feed your plan.</li>
          <li><strong style={{ color: 'var(--melio-charcoal)' }}>4.</strong> After sign-off, edits move to a review-based correction process — so the record stays trustworthy for your clinician.</li>
        </ol>
        <div className="flex justify-end">
          <PrimaryButton onClick={onClose}>Got it</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function TreeDrawer({
  intake,
  publishedFamilyTree,
  selfLabel,
  onClose,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  publishedFamilyTree: any;
  selfLabel: string;
  onClose: () => void;
}) {
  const tree = publishedFamilyTree?.status === 'published' ? publishedFamilyTree : buildFamilyTreePreviewFromIntake(intake);
  const melioNotesUnlocked = publishedFamilyTree?.status === 'published';

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(48, 46, 40, 0.4)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 max-w-4xl w-full"
        style={{ backgroundColor: 'white' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--melio-sage)' }}>
              {publishedFamilyTree?.status === 'published' ? 'Your clinician-reviewed tree' : 'Live preview'}
            </p>
            <h3 className="text-xl" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              Your family tree so far
            </h3>
          </div>
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </div>
        <FamilyTreeCanvas
          tree={tree}
          melioNotesUnlocked={melioNotesUnlocked}
          compact
          selfLabel={selfLabel}
        />
      </div>
    </div>
  );
}

/* =====================================================================
 * Screen renderers — small focused components
 * ===================================================================== */

function WelcomeScreen({ displayName }: { displayName: string }) {
  const name = displayName?.trim() || 'there';
  return (
    <ScreenShell
      eyebrow="Family history · Intro"
      title={`Hi ${name} — let's map your family history.`}
      subtitle="We'll ask a few short questions. It takes about 7 minutes, and we save as you go. Skip anything you don't know — your clinician will fill in gaps on the call."
    >
      <ul className="space-y-3 text-sm" style={{ color: 'var(--melio-text-muted)' }}>
        <li>· The basics about you — name, date of birth, sex at birth.</li>
        <li>· What you're currently treated for, any meds or allergies.</li>
        <li>· Who is in your blood family, and any big health events they've had.</li>
        <li>· We finish on a short checklist so you know nothing important was missed.</li>
      </ul>
    </ScreenShell>
  );
}

function NameScreen({
  intake,
  editable,
  onPatch,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  onPatch: (patch: Partial<PatientFamilyHistoryIntakeRecord['personalInfo']>) => void;
}) {
  const firstName =
    intake.personalInfo?.firstName ??
    (intake.personalInfo?.displayName?.trim().split(/\s+/)?.[0] ?? '');
  const lastName =
    intake.personalInfo?.lastName ??
    (intake.personalInfo?.displayName?.trim().split(/\s+/)?.slice(1).join(' ') ?? '');

  const patchName = (next: { firstName?: string; lastName?: string }) => {
    const nextFirst = next.firstName ?? firstName;
    const nextLast = next.lastName ?? lastName;
    const displayName = [nextFirst, nextLast].filter(Boolean).join(' ').trim();
    onPatch({ firstName: nextFirst, lastName: nextLast, displayName });
  };

  return (
    <ScreenShell eyebrow="1 · Basics" title="What should we call you?">
      <div className="grid md:grid-cols-2 gap-4 max-w-xl">
        <div>
          <FieldLabel>First name</FieldLabel>
          <TextInput
            value={firstName}
            disabled={!editable}
            autoFocus
            onChange={(event) => patchName({ firstName: event.target.value })}
          />
        </div>
        <div>
          <FieldLabel>Last name</FieldLabel>
          <TextInput
            value={lastName}
            disabled={!editable}
            onChange={(event) => patchName({ lastName: event.target.value })}
          />
        </div>
      </div>
    </ScreenShell>
  );
}

function DobSexScreen({
  intake,
  editable,
  onPatch,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  onPatch: (patch: Partial<PatientFamilyHistoryIntakeRecord['personalInfo']>) => void;
}) {
  const options: Array<{ value: string; label: string }> = [
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
  ];

  return (
    <ScreenShell
      eyebrow="2 · Basics"
      title="Your date of birth and sex at birth"
      subtitle="Sex-specific screening (breast, ovarian, prostate, cervical) depends on these, so they're required. If your clinical picture is more nuanced, mention it on the call and your clinician will note it."
    >
      <div className="space-y-5 max-w-md">
        <div>
          <FieldLabel>Date of birth</FieldLabel>
          <TextInput
            type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={intake.personalInfo?.dateOfBirth ?? ''}
            disabled={!editable}
            onChange={(event) => onPatch({ dateOfBirth: event.target.value })}
          />
        </div>
        <div>
          <FieldLabel>Sex at birth</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {options.map((option) => {
              const active = intake.personalInfo?.sexAtBirth === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={!editable}
                  onClick={() => onPatch({ sexAtBirth: option.value })}
                  className="px-4 py-2 rounded-xl text-sm border disabled:opacity-50"
                  style={{
                    backgroundColor: active ? 'var(--melio-charcoal)' : 'white',
                    color: active ? 'var(--melio-cream)' : 'var(--melio-charcoal)',
                    borderColor: active ? 'var(--melio-charcoal)' : 'var(--melio-border)',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </ScreenShell>
  );
}

function ContactScreen({
  intake,
  editable,
  onPatch,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  onPatch: (patch: Partial<PatientFamilyHistoryIntakeRecord['personalInfo']>) => void;
}) {
  // Defence in depth: getFamilyHistoryIntake normalises these, but this screen
  // is also reachable with records supplied by other callers. A missing
  // address map used to throw during render, and with no error boundary that
  // took down the entire member app.
  const personalInfo = intake.personalInfo ?? ({} as PatientFamilyHistoryIntakeRecord['personalInfo']);
  const address = personalInfo.address ?? { line1: '', line2: '', city: '', postcode: '', country: '' };
  const hasEmail = Boolean(personalInfo.email);
  return (
    <ScreenShell
      eyebrow="3 · Basics"
      title="How can we reach you?"
      subtitle="Email's required so we can send reports and scheduling links. The rest is optional — skip and come back later."
    >
      <div className="grid md:grid-cols-2 gap-4 max-w-xl">
        <div className="md:col-span-2">
          <FieldLabel>Email {hasEmail ? <span className="ml-1 text-xs font-normal" style={{ color: 'var(--melio-text-muted)' }}>(from your account)</span> : null}</FieldLabel>
          <TextInput
            type="email"
            value={personalInfo.email ?? ''}
            disabled={!editable}
            onChange={(event) => onPatch({ email: event.target.value })}
          />
        </div>
        <div>
          <FieldLabel>Phone</FieldLabel>
          <TextInput
            type="tel"
            value={personalInfo.phone ?? ''}
            disabled={!editable}
            onChange={(event) => onPatch({ phone: event.target.value })}
          />
        </div>
        <div />
        <div className="md:col-span-2">
          <FieldLabel>Address line 1</FieldLabel>
          <TextInput
            value={address.line1}
            disabled={!editable}
            onChange={(event) => onPatch({ address: { ...address, line1: event.target.value } })}
          />
        </div>
        <div>
          <FieldLabel>City</FieldLabel>
          <TextInput
            value={address.city}
            disabled={!editable}
            onChange={(event) => onPatch({ address: { ...address, city: event.target.value } })}
          />
        </div>
        <div>
          <FieldLabel>Postcode</FieldLabel>
          <TextInput
            value={address.postcode}
            disabled={!editable}
            onChange={(event) => onPatch({ address: { ...address, postcode: event.target.value } })}
          />
        </div>
      </div>
    </ScreenShell>
  );
}

const DOSE_UNIT_OPTIONS: Array<{ value: IntakeMedicationDoseUnit; label: string }> = [
  { value: 'mg', label: 'mg' },
  { value: 'mcg', label: 'mcg' },
  { value: 'g', label: 'g' },
  { value: 'ml', label: 'ml' },
  { value: 'units', label: 'units' },
  { value: 'puffs', label: 'puffs' },
  { value: 'tablets', label: 'tablets' },
  { value: 'drops', label: 'drops' },
  { value: 'patch', label: 'patch' },
  { value: 'other', label: 'other' },
];

const FREQUENCY_PERIOD_OPTIONS: Array<{ value: IntakeMedicationFrequencyPeriod; label: string }> = [
  { value: 'day', label: 'per day' },
  { value: 'week', label: 'per week' },
  { value: 'month', label: 'per month' },
  { value: 'prn', label: 'as needed (PRN)' },
];

function formatMedicationDoseLabel(medication: IntakePersonalMedication): string {
  if (medication.doseAmount != null && medication.doseUnit) {
    return `${medication.doseAmount} ${medication.doseUnit}`;
  }
  return medication.doseLabel || '';
}

function formatMedicationFrequencyLabel(medication: IntakePersonalMedication): string {
  if (medication.frequencyPeriod === 'prn') return 'as needed (PRN)';
  if (medication.frequencyTimes != null && medication.frequencyPeriod) {
    const period = medication.frequencyPeriod === 'day' ? 'day' : medication.frequencyPeriod === 'week' ? 'week' : 'month';
    return `${medication.frequencyTimes}x per ${period}`;
  }
  return medication.frequencyLabel || '';
}

function MedicationsScreen({
  editable,
  personalHistory,
  updatePersonalHistory,
}: {
  editable: boolean;
  personalHistory: IntakePersonalHistory;
  updatePersonalHistory: (patch: Partial<IntakePersonalHistory>) => void;
}) {
  const add = () => updatePersonalHistory({ medications: [...personalHistory.medications, createEmptyMedication()] });
  const setList = (next: IntakePersonalMedication[]) => updatePersonalHistory({ medications: next });
  const patchMed = (index: number, patch: Partial<IntakePersonalMedication>) => {
    setList(personalHistory.medications.map((item, idx) => {
      if (idx !== index) return item;
      const next = { ...item, ...patch };
      next.doseLabel = formatMedicationDoseLabel(next);
      next.frequencyLabel = formatMedicationFrequencyLabel(next);
      return next;
    }));
  };

  return (
    <ScreenShell
      eyebrow="Your health"
      title="What are you currently taking?"
      subtitle="Prescription, over-the-counter, and supplements. Skip if none."
    >
      <EditableList
        title="Medications and supplements"
        addLabel="Add medication"
        editable={editable}
        onAdd={add}
        items={personalHistory.medications}
        empty="Nothing to record. You can always add this later."
        onRemoveAt={(idx) => setList(personalHistory.medications.filter((_, i) => i !== idx))}
        renderItem={(medication, index, remove) => {
          const isPrn = medication.frequencyPeriod === 'prn';
          return (
            <div key={medication.id} className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
              <div className="grid md:grid-cols-[1.6fr_auto] gap-3 items-start">
                <div className="space-y-3">
                  <div>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                      value={medication.name}
                      disabled={!editable}
                      placeholder="e.g. Ibuprofen"
                      onChange={(event) => patchMed(index, { name: event.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-[1fr_160px] gap-3">
                    <div>
                      <FieldLabel>Dose amount</FieldLabel>
                      <TextInput
                        type="number"
                        min={0}
                        step="any"
                        disabled={!editable}
                        placeholder="200"
                        value={medication.doseAmount ?? ''}
                        onChange={(event) => patchMed(index, { doseAmount: event.target.value ? Math.max(0, Number(event.target.value)) : null })}
                      />
                    </div>
                    <div>
                      <FieldLabel>Unit</FieldLabel>
                      <Select
                        value={medication.doseUnit || 'mg'}
                        disabled={!editable}
                        onChange={(event) => patchMed(index, { doseUnit: event.target.value as IntakeMedicationDoseUnit })}
                      >
                        {DOSE_UNIT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-[120px_1fr] gap-3">
                    <div>
                      <FieldLabel>How many times</FieldLabel>
                      <Select
                        value={isPrn ? '' : String(medication.frequencyTimes ?? '')}
                        disabled={!editable || isPrn}
                        onChange={(event) => patchMed(index, { frequencyTimes: event.target.value ? Number(event.target.value) : null })}
                      >
                        <option value="">—</option>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <FieldLabel>Frequency</FieldLabel>
                      <Select
                        value={medication.frequencyPeriod || 'day'}
                        disabled={!editable}
                        onChange={(event) => {
                          const next = event.target.value as IntakeMedicationFrequencyPeriod;
                          patchMed(index, { frequencyPeriod: next, frequencyTimes: next === 'prn' ? null : medication.frequencyTimes });
                        }}
                      >
                        {FREQUENCY_PERIOD_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </Select>
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Reason / condition</FieldLabel>
                    <TextInput
                      value={medication.reason || ''}
                      disabled={!editable}
                      placeholder="e.g. back pain"
                      onChange={(event) => patchMed(index, { reason: event.target.value })}
                    />
                  </div>
                </div>
                {editable && (
                  <div className="md:pt-6">
                    <RemoveRowButton onClick={() => remove(index)} />
                  </div>
                )}
              </div>
            </div>
          );
        }}
      />
    </ScreenShell>
  );
}

function AllergiesScreen({
  editable,
  personalHistory,
  updatePersonalHistory,
}: {
  editable: boolean;
  personalHistory: IntakePersonalHistory;
  updatePersonalHistory: (patch: Partial<IntakePersonalHistory>) => void;
}) {
  const add = () => updatePersonalHistory({ allergies: [...personalHistory.allergies, createEmptyAllergy()] });
  const setList = (next: IntakePersonalAllergy[]) => updatePersonalHistory({ allergies: next });

  return (
    <ScreenShell
      eyebrow="Your health"
      title="Any allergies or reactions we should know about?"
      subtitle="Drug allergies, food allergies, severe reactions. Skip if none."
    >
      <EditableList
        title="Allergies and reactions"
        addLabel="Add allergy"
        editable={editable}
        onAdd={add}
        items={personalHistory.allergies}
        empty="No known allergies."
        onRemoveAt={(idx) => setList(personalHistory.allergies.filter((_, i) => i !== idx))}
        renderItem={(allergy, index, remove) => (
          <div key={allergy.id} className="grid md:grid-cols-[1fr_1.3fr_160px_auto] gap-3">
            <TextInput
              value={allergy.allergen}
              disabled={!editable}
              placeholder="Allergen (e.g. penicillin, peanuts)"
              onChange={(event) => setList(personalHistory.allergies.map((item, idx) => idx === index ? { ...item, allergen: event.target.value } : item))}
            />
            <TextInput
              value={allergy.reactionLabel || ''}
              disabled={!editable}
              placeholder="Reaction"
              onChange={(event) => setList(personalHistory.allergies.map((item, idx) => idx === index ? { ...item, reactionLabel: event.target.value } : item))}
            />
            <Select
              value={allergy.severity || 'unknown'}
              disabled={!editable}
              onChange={(event) => setList(personalHistory.allergies.map((item, idx) => idx === index ? { ...item, severity: event.target.value as IntakePersonalAllergy['severity'] } : item))}
            >
              <option value="unknown">Severity unknown</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </Select>
            {editable && <RemoveRowButton onClick={() => remove(index)} />}
          </div>
        )}
      />
    </ScreenShell>
  );
}

function DiagnosesScreen({
  editable,
  personalHistory,
  updatePersonalHistory,
}: {
  editable: boolean;
  personalHistory: IntakePersonalHistory;
  updatePersonalHistory: (patch: Partial<IntakePersonalHistory>) => void;
}) {
  const add = () => updatePersonalHistory({ activeDiagnoses: [...personalHistory.activeDiagnoses, createEmptyDiagnosis()] });
  const setList = (next: IntakePersonalDiagnosis[]) => updatePersonalHistory({ activeDiagnoses: next });

  return (
    <ScreenShell
      eyebrow="Your health"
      title="Conditions you're actively treated for"
      subtitle="Anything your doctor is actively managing. Skip if none."
    >
      <EditableList
        title="Active diagnoses"
        addLabel="Add diagnosis"
        editable={editable}
        onAdd={add}
        items={personalHistory.activeDiagnoses}
        empty="Nothing active to record."
        onRemoveAt={(idx) => setList(personalHistory.activeDiagnoses.filter((_, i) => i !== idx))}
        renderItem={(diagnosis, index, remove) => (
          <div key={diagnosis.id} className="grid md:grid-cols-[1.2fr_1fr_120px_auto] gap-3">
            <ConditionCodeSelect
              value={diagnosis.code}
              label={diagnosis.label}
              disabled={!editable}
              onChange={(code, label) => setList(personalHistory.activeDiagnoses.map((item, idx) => idx === index ? { ...item, code, label } : item))}
            />
            <TextArea
              rows={2}
              value={diagnosis.notes || ''}
              disabled={!editable}
              placeholder="Notes (optional)"
              onChange={(event) => setList(personalHistory.activeDiagnoses.map((item, idx) => idx === index ? { ...item, notes: event.target.value } : item))}
            />
            <TextInput
              type="number"
              min={0}
              step={1}
              value={diagnosis.ageAtDiagnosis ?? ''}
              disabled={!editable}
              placeholder="Age at dx"
              onChange={(event) => setList(personalHistory.activeDiagnoses.map((item, idx) => idx === index ? { ...item, ageAtDiagnosis: event.target.value ? Math.max(0, Math.floor(Number(event.target.value))) : null } : item))}
            />
            {editable && <RemoveRowButton onClick={() => remove(index)} />}
          </div>
        )}
      />
    </ScreenShell>
  );
}

/**
 * Operations and hospital stays.
 *
 * `surgeries` has been on the record since the type was written — typed,
 * defaulted by the service, and never once asked for. So the field was
 * persisted-but-uncollected, and the ops overview tile counted a section the
 * member was never shown.
 *
 * An operation changes what a marker means: a thyroidectomy, a cholecystectomy,
 * bariatric surgery or an oophorectomy each bear on a domain the panel reports
 * on. Asking here also turns the clinician's version of this question into
 * confirm-and-correct rather than a cold start.
 *
 * `hospitalisations` and `traumaHistory` sit beside it on the record and are
 * likewise never asked. They are left alone deliberately — one new screen for
 * the thing that changes a reading, rather than three because the fields exist.
 */
function SurgeriesScreen({
  editable,
  surgeries,
  updateIntake,
}: {
  editable: boolean;
  surgeries: IntakeMedicalTimelineEntry[];
  updateIntake: (patch: Partial<PatientFamilyHistoryIntakeRecord>) => void;
}) {
  const setList = (next: IntakeMedicalTimelineEntry[]) => updateIntake({ surgeries: next });

  return (
    <ScreenShell
      eyebrow="Your health"
      title="Operations and hospital stays"
      subtitle="Anything you have been under for, however long ago. Rough dates are fine — “my twenties” is a useful answer."
    >
      <EditableList
        title="Operations"
        addLabel="Add an operation"
        editable={editable}
        onAdd={() => setList([...surgeries, createEmptyTimelineEntry()])}
        items={surgeries}
        empty="Nothing to record."
        onRemoveAt={(idx) => setList(surgeries.filter((_, i) => i !== idx))}
        renderItem={(entry, index, remove) => (
          <div key={entry.id} className="grid md:grid-cols-[1.2fr_140px_1fr_auto] gap-3">
            <TextInput
              value={entry.label}
              disabled={!editable}
              placeholder="What was it? (e.g. gallbladder removed)"
              onChange={(event) => setList(surgeries.map((item, idx) => (idx === index ? { ...item, label: event.target.value } : item)))}
            />
            <TextInput
              value={entry.dateLabel}
              disabled={!editable}
              placeholder="When?"
              onChange={(event) => setList(surgeries.map((item, idx) => (idx === index ? { ...item, dateLabel: event.target.value } : item)))}
            />
            <TextInput
              value={entry.notes}
              disabled={!editable}
              placeholder="What was it for?"
              onChange={(event) => setList(surgeries.map((item, idx) => (idx === index ? { ...item, notes: event.target.value } : item)))}
            />
            {editable && <RemoveRowButton onClick={() => remove(index)} />}
          </div>
        )}
      />
    </ScreenShell>
  );
}

function GeneticTestsScreen({
  editable,
  personalHistory,
  updatePersonalHistory,
}: {
  editable: boolean;
  personalHistory: IntakePersonalHistory;
  updatePersonalHistory: (patch: Partial<IntakePersonalHistory>) => void;
}) {
  const add = () => updatePersonalHistory({ priorGeneticTests: [...personalHistory.priorGeneticTests, createEmptyGeneticTest()] });
  const setList = (next: IntakePriorGeneticTest[]) => updatePersonalHistory({ priorGeneticTests: next });

  return (
    <ScreenShell
      eyebrow="Your health"
      title="Previous genetic testing"
      subtitle="Anything from consumer kits (23andMe) to clinical panels. Skip if none."
    >
      <EditableList
        title="Prior genetic tests"
        addLabel="Add test"
        editable={editable}
        onAdd={add}
        items={personalHistory.priorGeneticTests}
        empty="No prior genetic testing on file."
        onRemoveAt={(idx) => setList(personalHistory.priorGeneticTests.filter((_, i) => i !== idx))}
        renderItem={(test, index, remove) => (
          // Known variants gets its own full-width row. It is the one field
          // here that holds real clinical prose — gene, variant, zygosity,
          // classification — and it was sharing a five-column row with "Year".
          <div key={test.id} className="space-y-3">
            <div className="grid md:grid-cols-[1fr_0.7fr_1fr_auto] gap-3">
              <TextInput
                value={test.testName}
                disabled={!editable}
                placeholder="Test name (23andMe, BRCA panel…)"
                onChange={(event) => setList(personalHistory.priorGeneticTests.map((item, idx) => idx === index ? { ...item, testName: event.target.value } : item))}
              />
              <TextInput
                value={test.yearLabel || ''}
                disabled={!editable}
                placeholder="Year"
                onChange={(event) => setList(personalHistory.priorGeneticTests.map((item, idx) => idx === index ? { ...item, yearLabel: event.target.value } : item))}
              />
              <TextInput
                value={test.providerLabel || ''}
                disabled={!editable}
                placeholder="Provider"
                onChange={(event) => setList(personalHistory.priorGeneticTests.map((item, idx) => idx === index ? { ...item, providerLabel: event.target.value } : item))}
              />
              {editable && <RemoveRowButton onClick={() => remove(index)} />}
            </div>
            <TextArea
              rows={2}
              value={test.knownVariants || ''}
              disabled={!editable}
              placeholder="Known variants / findings — copy anything the report said, in their words"
              onChange={(event) => setList(personalHistory.priorGeneticTests.map((item, idx) => idx === index ? { ...item, knownVariants: event.target.value } : item))}
            />
          </div>
        )}
      />
    </ScreenShell>
  );
}

const ANCESTRY_LINEAGE_OPTIONS: Array<{ value: IntakeAncestryLineage; label: string }> = [
  { value: 'both', label: 'Both sides' },
  { value: 'maternal', label: 'Mother\u2019s side' },
  { value: 'paternal', label: 'Father\u2019s side' },
  { value: 'unknown', label: 'Not sure' },
];

const ANCESTRY_REGION_ORDER: IntakeAncestryRegion[] = [
  'european_northern',
  'european_southern',
  'european_eastern',
  'ashkenazi_jewish',
  'sephardi_jewish',
  'mizrahi_jewish',
  'sub_saharan_african',
  'north_african',
  'middle_eastern',
  'south_asian',
  'east_asian',
  'southeast_asian',
  'central_asian',
  'pacific_islander',
  'native_american',
  'hispanic_latino',
  'caribbean',
  'other',
];

function AncestryScreen({
  editable,
  personalHistory,
  updatePersonalHistory,
}: {
  editable: boolean;
  personalHistory: IntakePersonalHistory;
  updatePersonalHistory: (patch: Partial<IntakePersonalHistory>) => void;
}) {
  const ancestries: IntakeAncestryEntry[] = personalHistory.ancestries ?? [];

  const toggleRegion = (region: IntakeAncestryRegion) => {
    const existing = ancestries.find((entry) => entry.region === region);
    if (existing) {
      updatePersonalHistory({ ancestries: ancestries.filter((entry) => entry.region !== region) });
    } else {
      updatePersonalHistory({
        ancestries: [...ancestries, { id: crypto.randomUUID(), region, lineage: 'both' }],
      });
    }
  };

  const patchEntry = (id: string, patch: Partial<IntakeAncestryEntry>) => {
    updatePersonalHistory({
      ancestries: ancestries.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    });
  };

  return (
    <ScreenShell
      eyebrow="Your health"
      title="Ancestry and heritage"
      subtitle="Some hereditary patterns are much more common in specific ancestries (e.g. Ashkenazi for BRCA, South Asian for CVD). Tap any that apply — mixed is normal."
    >
      <div className="flex flex-wrap gap-2 mb-5">
        {ANCESTRY_REGION_ORDER.map((region) => {
          const meta = ANCESTRY_CATALOG[region];
          const active = ancestries.some((entry) => entry.region === region);
          return (
            <button
              key={region}
              type="button"
              disabled={!editable}
              onClick={() => toggleRegion(region)}
              className="px-3 py-2 rounded-full text-sm border disabled:opacity-50"
              style={{
                borderColor: active ? 'var(--melio-charcoal)' : 'var(--melio-border)',
                backgroundColor: active ? 'var(--melio-charcoal)' : 'white',
                color: active ? 'var(--melio-cream)' : 'var(--melio-charcoal)',
              }}
              title={meta.hint}
            >
              {active ? '✓ ' : '+ '}{meta.label}
            </button>
          );
        })}
      </div>

      {ancestries.length > 0 && (
        <div className="space-y-3 mb-5">
          {ancestries.map((entry) => {
            const meta = ANCESTRY_CATALOG[entry.region];
            return (
              <div key={entry.id} className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{meta.label}</p>
                  {editable && (
                    <button
                      type="button"
                      onClick={() => toggleRegion(entry.region)}
                      className="text-xs underline"
                      style={{ color: 'var(--melio-text-muted)', background: 'transparent' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {meta.hint && (
                  <p className="text-xs mb-3" style={{ color: 'var(--melio-text-muted)' }}>{meta.hint}</p>
                )}
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Which side of the family?</FieldLabel>
                    <Select
                      value={entry.lineage || 'both'}
                      disabled={!editable}
                      onChange={(event) => patchEntry(entry.id, { lineage: event.target.value as IntakeAncestryLineage })}
                    >
                      {ANCESTRY_LINEAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <FieldLabel>More detail (optional)</FieldLabel>
                    <TextArea
                      rows={2}
                      value={entry.detail || ''}
                      disabled={!editable}
                      placeholder="e.g. Punjabi, Ashkenazi specifically"
                      onChange={(event) => patchEntry(entry.id, { detail: event.target.value })}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div>
        <FieldLabel>Anything else worth mentioning?</FieldLabel>
        <TextArea
          rows={4}
          value={personalHistory.ancestryNotes || ''}
          disabled={!editable}
          placeholder="Optional — use this only if the chips above don't capture it."
          onChange={(event) => updatePersonalHistory({ ancestryNotes: event.target.value })}
        />
      </div>
    </ScreenShell>
  );
}

function FamilyStructureScreen({
  intake,
  editable,
  onPatch,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  onPatch: (patch: Partial<PatientFamilyHistoryIntakeRecord['familyStructure']>) => void;
}) {
  const fs = intake.familyStructure;
  const num = (value: number | null) => (value == null ? '' : String(value));
  const setNum = (key: keyof PatientFamilyHistoryIntakeRecord['familyStructure'], value: string) => {
    const parsed = value === '' ? null : Math.max(0, Math.floor(Number(value)));
    onPatch({ [key]: Number.isNaN(parsed as number) ? null : parsed } as any);
  };
  const numProps = { type: 'number' as const, min: 0, step: 1 };

  return (
    <ScreenShell
      eyebrow="Your family"
      title="Quick numbers before we add people"
      subtitle="Approximate is fine — we just want the shape of your family tree."
    >
      <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
        <div>
          <FieldLabel>How many siblings?</FieldLabel>
          <TextInput {...numProps} disabled={!editable} value={num(fs.siblingsCount)} onChange={(event) => setNum('siblingsCount', event.target.value)} />
        </div>
        <div>
          <FieldLabel>Children?</FieldLabel>
          <TextInput {...numProps} disabled={!editable} value={num(fs.childrenCount)} onChange={(event) => setNum('childrenCount', event.target.value)} />
        </div>
        <div>
          <FieldLabel>Maternal aunts</FieldLabel>
          <TextInput {...numProps} disabled={!editable} value={num(fs.maternalAuntsCount)} onChange={(event) => setNum('maternalAuntsCount', event.target.value)} />
        </div>
        <div>
          <FieldLabel>Maternal uncles</FieldLabel>
          <TextInput {...numProps} disabled={!editable} value={num(fs.maternalUnclesCount)} onChange={(event) => setNum('maternalUnclesCount', event.target.value)} />
        </div>
        <div>
          <FieldLabel>Paternal aunts</FieldLabel>
          <TextInput {...numProps} disabled={!editable} value={num(fs.paternalAuntsCount)} onChange={(event) => setNum('paternalAuntsCount', event.target.value)} />
        </div>
        <div>
          <FieldLabel>Paternal uncles</FieldLabel>
          <TextInput {...numProps} disabled={!editable} value={num(fs.paternalUnclesCount)} onChange={(event) => setNum('paternalUnclesCount', event.target.value)} />
        </div>
        <div>
          <FieldLabel>Did you know all four grandparents?</FieldLabel>
          <Select value={fs.knewAllGrandparents} disabled={!editable} onChange={(event) => onPatch({ knewAllGrandparents: event.target.value as any })}>
            <option value="unknown">Unknown</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </div>
        <div>
          <FieldLabel>Do any siblings have children?</FieldLabel>
          <Select value={fs.siblingsHaveChildren} disabled={!editable} onChange={(event) => onPatch({ siblingsHaveChildren: event.target.value as any })}>
            <option value="unknown">Unknown</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </div>
      </div>
    </ScreenShell>
  );
}

function FamilyTreeScreen({
  intake,
  editable,
  setIntake,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  setIntake: React.Dispatch<React.SetStateAction<PatientFamilyHistoryIntakeRecord | null>>;
}) {
  const addRelative = (relation: IntakeRelation) => {
    const autoLineage = autoLineageForRelation(relation) || 'unknown';
    const relative = createEmptyRelative({
      relation,
      lineageSide: autoLineage,
      sex: autoSexForRelation(relation, 'unknown'),
    });
    setIntake((current) => (current ? { ...current, relatives: [...current.relatives, relative] } : current));
  };

  const updateRelative = (relativeId: string, next: IntakeRelativeRecord) => {
    setIntake((current) => {
      if (!current) return current;
      return { ...current, relatives: current.relatives.map((relative) => (relative.id === relativeId ? next : relative)) };
    });
  };

  const removeRelative = (relativeId: string) => {
    setIntake((current) => {
      if (!current) return current;
      return {
        ...current,
        relatives: current.relatives.filter((relative) => relative.id !== relativeId),
        conditionAssignments: current.conditionAssignments.map((assignment) => ({
          ...assignment,
          familyMemberIds: assignment.familyMemberIds.filter((id) => id !== relativeId),
          relativeDetails: (assignment.relativeDetails || []).filter((detail) => detail.relativeId !== relativeId),
        })),
      };
    });
  };

  const hasRelation = (relation: IntakeRelation) => intake.relatives.some((relative) => relative.relation === relation);

  /**
   * Record a parent whose history the member genuinely does not have.
   *
   * Still a real relative — an adopted member has a biological mother whose
   * history is unknown, which is a different fact from having no mother. The
   * record is created with everything set to unknown and a note saying why, so
   * the clinician sees "asked, not known" rather than "never asked".
   */
  const addUnknownParent = (relation: IntakeRelation) => {
    const relative = createEmptyRelative({
      relation,
      lineageSide: autoLineageForRelation(relation) || 'unknown',
      sex: autoSexForRelation(relation, 'unknown'),
    });
    setIntake((current) => (current ? {
      ...current,
      relatives: [...current.relatives, {
        ...relative,
        aliveStatus: 'unknown',
        priorGeneticTesting: 'unknown',
        notes: 'Member does not have this parent’s medical history.',
      }],
    } : current));
  };

  const quickAdd: Array<{ relation: IntakeRelation; label: string }> = [
    { relation: 'mother', label: 'Mother' },
    { relation: 'father', label: 'Father' },
    { relation: 'sister', label: 'Sister' },
    { relation: 'brother', label: 'Brother' },
    { relation: 'maternal_grandmother', label: 'Maternal grandmother' },
    { relation: 'maternal_grandfather', label: 'Maternal grandfather' },
    { relation: 'paternal_grandmother', label: 'Paternal grandmother' },
    { relation: 'paternal_grandfather', label: 'Paternal grandfather' },
    { relation: 'daughter', label: 'Daughter' },
    { relation: 'son', label: 'Son' },
  ];

  return (
    <ScreenShell
      eyebrow="Your family"
      title="Who's in your blood family?"
      subtitle="Add parents and grandparents at minimum. Use the quick-add buttons, or add custom relatives below."
    >
      {editable && (
        <div className="flex flex-wrap gap-2 mb-5">
          {quickAdd.map(({ relation, label }) => {
            const added = hasRelation(relation);
            return (
              <button
                key={relation}
                type="button"
                disabled={added}
                onClick={() => addRelative(relation)}
                className="px-3 py-2 rounded-full text-sm border disabled:opacity-50"
                style={{
                  borderColor: added ? 'rgba(79, 93, 24, 0.5)' : 'var(--melio-border)',
                  backgroundColor: added ? 'rgba(79, 93, 24, 0.12)' : 'white',
                  color: 'var(--melio-charcoal)',
                }}
              >
                {added ? '✓ ' : '+ '}{label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => addRelative('other')}
            className="px-3 py-2 rounded-full text-sm border"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-charcoal)' }}
          >
            + Other relative
          </button>
        </div>
      )}

      {/* Parents are the one thing this screen genuinely requires, and until now
          it never said so — a member could walk the whole form without adding a
          single relative and be told they were 60% done, because the readiness
          gate gives mother and father two of its five marks.

          The escape hatch matters as much as the requirement. Adoption, donor
          conception and estrangement are common and none of them mean a member
          should be trapped here: "I don't know" is a real clinical answer, and
          recording it is different from never having been asked. */}
      {editable && (!hasRelation('mother') || !hasRelation('father')) && (
        <div
          className="text-sm rounded-2xl border p-4 mb-4"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'rgba(79, 93, 24, 0.06)' }}
        >
          <p style={{ color: 'var(--melio-charcoal)', fontWeight: 500, marginBottom: 6 }}>
            We need your {!hasRelation('mother') && !hasRelation('father')
              ? 'mother and father'
              : !hasRelation('mother') ? 'mother' : 'father'} before you can continue.
          </p>
          <p style={{ color: 'var(--melio-text-muted)', marginBottom: 10 }}>
            Your parents’ history is what most of the patterns we look for are
            read against. If you do not know it, say so — that is an answer, and
            it is better than a blank.
          </p>
          <div className="flex flex-wrap gap-2">
            {(['mother', 'father'] as IntakeRelation[])
              .filter((relation) => !hasRelation(relation))
              .map((relation) => (
                <button
                  key={relation}
                  type="button"
                  onClick={() => addUnknownParent(relation)}
                  className="px-3 py-2 rounded-full text-sm border"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-charcoal)' }}
                >
                  I don’t know my {relation}’s history
                </button>
              ))}
          </div>
        </div>
      )}

      {intake.relatives.length === 0 ? (
        <p className="text-sm rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-text-muted)' }}>
          Start by tapping a quick-add button above. Parents first, then grandparents.
        </p>
      ) : (
        <div className="space-y-3">
          {intake.relatives.map((relative) => (
            <RelativeRow
              key={relative.id}
              relative={relative}
              editable={editable}
              onChange={(next) => updateRelative(relative.id, next)}
              onRemove={() => removeRelative(relative.id)}
            />
          ))}
        </div>
      )}
    </ScreenShell>
  );
}

function ConditionsScreen({
  group,
  intake,
  editable,
  setIntake,
}: {
  group: 'heart' | 'cancer' | 'other';
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  setIntake: React.Dispatch<React.SetStateAction<PatientFamilyHistoryIntakeRecord | null>>;
}) {
  const copy = CONDITION_GROUP_COPY[group];
  const codesInGroup = CONDITION_GROUPS[group];
  const inGroup = (assignment: IntakeFamilyConditionAssignment) => Boolean(assignment.code && codesInGroup.includes(assignment.code));
  const assignments = intake.conditionAssignments.filter(inGroup);

  const updateAssignment = (next: IntakeFamilyConditionAssignment) => {
    setIntake((current) => {
      if (!current) return current;
      return {
        ...current,
        conditionAssignments: current.conditionAssignments.map((assignment) =>
          assignment.id === next.id ? next : assignment,
        ),
      };
    });
  };

  const markAllNone = () => {
    setIntake((current) => {
      if (!current) return current;
      return {
        ...current,
        conditionAssignments: current.conditionAssignments.map((assignment) =>
          inGroup(assignment)
            ? { ...assignment, response: 'neither' as const, familyMemberIds: [], relativeDetails: [] }
            : assignment,
        ),
      };
    });
  };

  const allNone = assignments.every((assignment) => assignment.response === 'neither');

  return (
    <ScreenShell eyebrow="Conditions" title={copy.title} subtitle={copy.subtitle}>
      {editable && (
        <div className="mb-4">
          <button
            type="button"
            onClick={markAllNone}
            className="px-4 py-2 rounded-xl text-sm border"
            style={{
              borderColor: allNone ? 'var(--melio-charcoal)' : 'var(--melio-border)',
              backgroundColor: allNone ? 'var(--melio-charcoal)' : 'white',
              color: allNone ? 'var(--melio-cream)' : 'var(--melio-charcoal)',
            }}
          >
            {allNone ? '✓ ' : ''}{copy.none}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {assignments.map((assignment) => (
          <ConditionCard
            key={assignment.id}
            assignment={assignment}
            relatives={intake.relatives}
            editable={editable}
            onChange={updateAssignment}
          />
        ))}
      </div>
    </ScreenShell>
  );
}

function LifestyleScreen({
  intake,
  editable,
  updateNested,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  editable: boolean;
  updateNested: <T extends object>(field: keyof PatientFamilyHistoryIntakeRecord, patch: T) => void;
}) {
  return (
    <ScreenShell
      eyebrow="Lifestyle"
      title="A quick snapshot of day-to-day"
      subtitle="One-line answers are perfect. Skip anything that feels too personal to put in text."
    >
      {/* DRINKS, NOT UNITS.

          This was one text box with an "e.g. 4 units/week" placeholder. Almost
          nobody knows what a unit is, so it invited a number the member had
          invented on the spot — and a made-up figure is worse than no figure,
          because it looks like data.

          Three separate answers rather than one sentence, matching the ops
          interview's wording exactly so a clinician is confirming what was said
          here rather than asking again. No score is derived or shown. */}
      <div className="max-w-3xl mb-6">
        <FieldLabel>How often do you have a drink containing alcohol?</FieldLabel>
        <Segmented
          disabled={!editable}
          value={intake.lifestyle.drinkFrequency || ''}
          onChange={(value) => updateNested('lifestyle', { drinkFrequency: value as IntakeDrinkFrequency })}
          options={[
            { value: 'never', label: 'Never' },
            { value: 'monthly_or_less', label: 'Monthly or less' },
            { value: 'two_to_four_monthly', label: '2–4 a month' },
            { value: 'two_to_three_weekly', label: '2–3 a week' },
            { value: 'four_plus_weekly', label: '4+ a week' },
          ]}
        />
      </div>

      {intake.lifestyle.drinkFrequency && intake.lifestyle.drinkFrequency !== 'never' && (
        <>
          <div className="max-w-3xl mb-6">
            <FieldLabel>On a day when you drink, how many drinks do you usually have?</FieldLabel>
            <Segmented
              disabled={!editable}
              value={intake.lifestyle.drinksPerSession || ''}
              onChange={(value) => updateNested('lifestyle', { drinksPerSession: value as IntakeDrinksPerSession })}
              options={[
                { value: 'one_to_two', label: '1–2' },
                { value: 'three_to_four', label: '3–4' },
                { value: 'five_to_six', label: '5–6' },
                { value: 'seven_to_nine', label: '7–9' },
                { value: 'ten_plus', label: '10+' },
              ]}
            />
          </div>

          <div className="max-w-3xl mb-6">
            <FieldLabel>How often do you have six or more drinks on one occasion?</FieldLabel>
            <Segmented
              disabled={!editable}
              value={intake.lifestyle.heavySessionFrequency || ''}
              onChange={(value) => updateNested('lifestyle', { heavySessionFrequency: value as IntakeHeavySessionFrequency })}
              options={[
                { value: 'never', label: 'Never' },
                { value: 'less_than_monthly', label: 'Less than monthly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'daily_or_almost', label: 'Most days' },
              ]}
            />
          </div>

          <div className="max-w-3xl mb-6">
            <FieldLabel>What do you usually drink?</FieldLabel>
            <TextInput
              value={intake.lifestyle.drinkType || ''}
              disabled={!editable}
              placeholder="e.g. large glasses of red, pints of lager"
              onChange={(event) => updateNested('lifestyle', { drinkType: event.target.value })}
            />
          </div>
        </>
      )}

      {/* "Rather not say" is offered on purpose. Without it a member who does not
          want to answer picks "No", and a false negative is worse than a
          recorded refusal — one is wrong, the other is honest. */}
      <div className="max-w-3xl mb-6">
        <FieldLabel>Do you use any recreational drugs?</FieldLabel>
        <Segmented
          disabled={!editable}
          value={intake.lifestyle.drugUse || ''}
          onChange={(value) => updateNested('lifestyle', { drugUse: value as IntakeDrugUse })}
          options={[
            { value: 'none', label: 'No' },
            { value: 'occasional', label: 'Occasionally' },
            { value: 'regular', label: 'Regularly' },
            { value: 'declined', label: 'Rather not say' },
          ]}
        />
      </div>

      {(intake.lifestyle.drugUse === 'occasional' || intake.lifestyle.drugUse === 'regular') && (
        <div className="max-w-3xl mb-6">
          <FieldLabel>Which, and how often?</FieldLabel>
          <TextInput
            value={intake.lifestyle.drugDetail || ''}
            disabled={!editable}
            onChange={(event) => updateNested('lifestyle', { drugDetail: event.target.value })}
          />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
        <div>
          <FieldLabel>Smoking</FieldLabel>
          <TextInput value={intake.lifestyle.smokingStatus} disabled={!editable} onChange={(event) => updateNested('lifestyle', { smokingStatus: event.target.value })} placeholder="Never / quit X years ago / currently" />
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Anything else about drinking?</FieldLabel>
          <TextInput value={intake.lifestyle.alcoholUse} disabled={!editable} onChange={(event) => updateNested('lifestyle', { alcoholUse: event.target.value })} placeholder="Optional" />
        </div>
        <div>
          <FieldLabel>Exercise</FieldLabel>
          <TextInput value={intake.lifestyle.exercisePattern} disabled={!editable} onChange={(event) => updateNested('lifestyle', { exercisePattern: event.target.value })} placeholder="e.g. Running 3x/week" />
        </div>
        <div>
          <FieldLabel>Sleep</FieldLabel>
          <TextInput value={intake.lifestyle.sleepPattern} disabled={!editable} onChange={(event) => updateNested('lifestyle', { sleepPattern: event.target.value })} placeholder="e.g. 6 hours, wake a lot" />
        </div>
        <div>
          <FieldLabel>Stress</FieldLabel>
          <TextInput value={intake.lifestyle.stressLevel} disabled={!editable} onChange={(event) => updateNested('lifestyle', { stressLevel: event.target.value })} placeholder="e.g. high at work, manageable" />
        </div>
        <div>
          <FieldLabel>Diet</FieldLabel>
          <TextInput value={intake.lifestyle.dietPattern} disabled={!editable} onChange={(event) => updateNested('lifestyle', { dietPattern: event.target.value })} placeholder="Anything noteworthy" />
        </div>
        <div>
          <FieldLabel>Occupation</FieldLabel>
          <TextInput value={intake.context.occupation} disabled={!editable} onChange={(event) => updateNested('context', { occupation: event.target.value })} />
        </div>
        <div>
          <FieldLabel>Household</FieldLabel>
          <TextInput value={intake.context.householdContext} disabled={!editable} onChange={(event) => updateNested('context', { householdContext: event.target.value })} placeholder="Who you live with" />
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Exposures or anything else you want to note</FieldLabel>
          <TextArea rows={3} value={intake.lifestyle.exposuresNotes} disabled={!editable} onChange={(event) => updateNested('lifestyle', { exposuresNotes: event.target.value })} />
        </div>
      </div>
    </ScreenShell>
  );
}

function ReviewScreen({
  intake,
  readiness,
  editable,
  submitting,
  onSubmit,
  onJumpTo,
  onPatientNotes,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  readiness: IntakeReadinessVerdict;
  editable: boolean;
  submitting: boolean;
  onSubmit: () => void;
  onJumpTo: (id: ScreenId) => void;
  onPatientNotes: (next: string) => void;
}) {
  const jumpForCheck = (checkId: string): ScreenId => {
    if (checkId === 'personal.dob' || checkId === 'personal.sex') return 'basics-dob-sex';
    if (checkId.startsWith('personal.')) return 'basics-name';
    if (checkId.startsWith('family.')) return 'family-tree';
    return 'review';
  };

  // Once it is submitted this screen is a receipt, not a to-do list. It kept
  // its "Before you submit" heading and its readiness checklist after the
  // submit button had gone, so the last thing a member saw on finishing was a
  // page telling them to fix things, with no way to act and no way out.
  return (
    <ScreenShell
      eyebrow={editable ? 'Almost done' : 'All done'}
      title={editable ? 'Before you submit' : 'Your history is submitted'}
      subtitle={
        editable
          ? "Here's what your clinician needs before the call. Fix any red items if you can."
          : 'This is what your care team received. Nothing here needs your attention.'
      }
    >
      <div className="space-y-3 mb-6" hidden={!editable}>
        <ReadinessRow
          label="Blockers"
          checks={readiness.blockers}
          tone="blocker"
          emptyLabel="No blockers — you can submit."
          onJump={(id) => onJumpTo(jumpForCheck(id))}
        />
        <ReadinessRow
          label="Recommended"
          checks={readiness.warnings}
          tone="warning"
          emptyLabel="Nothing recommended to add."
          onJump={(id) => onJumpTo(jumpForCheck(id))}
        />
      </div>

      <div className="mb-6">
        <FieldLabel>Anything else your clinician should know?</FieldLabel>
        <TextArea
          rows={5}
          value={intake.patientNotes}
          disabled={!editable}
          onChange={(event) => onPatientNotes(event.target.value)}
        />
      </div>

      {editable && (
        <div className="flex flex-wrap gap-3">
          <PrimaryButton onClick={onSubmit} disabled={submitting || !readiness.ready}>
            {submitting ? 'Submitting…' : 'Submit for review'}
          </PrimaryButton>
          {!readiness.ready && (
            <span className="text-sm" style={{ color: '#8f4d4d', alignSelf: 'center' }}>
              Fix the blockers above to unlock submit.
            </span>
          )}
        </div>
      )}
    </ScreenShell>
  );
}

function ReadinessRow({
  label,
  checks,
  tone,
  emptyLabel,
  onJump,
}: {
  label: string;
  checks: IntakeReadinessVerdict['blockers'];
  tone: 'blocker' | 'warning';
  emptyLabel: string;
  onJump: (checkId: string) => void;
}) {
  const color = tone === 'blocker' ? '#8f4d4d' : '#7a5a1b';
  return (
    <div>
      <p className="text-xs uppercase tracking-widest mb-2" style={{ color }}>{label}</p>
      {checks.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {checks.map((check) => (
            <li key={check.id} className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
              <span
                aria-hidden
                className="inline-block rounded-full mt-1.5"
                style={{ width: 8, height: 8, backgroundColor: color }}
              />
              <div className="flex-1">
                <p className="text-sm" style={{ color: 'var(--melio-charcoal)' }}>{check.label}</p>
                {check.detail && <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>{check.detail}</p>}
                {check.remediation && <p className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>{check.remediation}</p>}
              </div>
              <button
                type="button"
                onClick={() => onJump(check.id)}
                className="text-xs underline"
                style={{ color, background: 'transparent' }}
              >
                Fix
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* =====================================================================
 * Main orchestrator
 * ===================================================================== */

export default function FamilyHistoryIntakePage() {
  const { currentUser } = useAuth();
  const { familyTree: publishedFamilyTree, loading: overviewLoading } = useMemberSurveillanceOverview(currentUser?.uid);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [intake, setIntake] = useState<PatientFamilyHistoryIntakeRecord | null>(null);
  /**
   * THE STEP LIVES IN THE URL, not in component state.
   *
   * It used to be `useState('overview')`, which made two links on this page dead
   * and could not have worked. `/history` resolves to THIS component
   * (HealthHistoryPage re-exports it), so "Back to your history" and "All
   * sections" both linked from `/history` to `/history`: react-router matched the
   * same route element, reconciled instead of remounting, and `screenId` kept its
   * value. The member clicked and nothing happened.
   *
   * Mirrors the ops capture flow's `?step=` (GeneticsWorkflowPage), including its
   * `replace: true` — the wizard already has its own Back control, and pushing a
   * history entry per step would leave the browser button fighting it.
   *
   * `overview` is deliberately absent from SCREENS, so it is the no-param state:
   * `/history` is the hub, `/history?step=lifestyle` is a step. That is what makes
   * a plain <Link to="/history"> reach the hub from anywhere.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedScreen = searchParams.get('step');
  const screenId: ScreenId = SCREENS.some((screen) => screen.id === requestedScreen)
    ? (requestedScreen as ScreenId)
    : 'overview';

  const setScreenId = useCallback((next: ScreenId) => {
    if (next === 'overview') setSearchParams({}, { replace: true });
    else setSearchParams({ step: next }, { replace: true });
    // Long screens otherwise leave the member halfway down the next one.
    window.scrollTo({ top: 0 });
  }, [setSearchParams]);
  const [treeOpen, setTreeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [changeRequests, setChangeRequests] = useState<FamilyHistoryChangeRequestRecord[]>([]);
  const [requestArea, setRequestArea] = useState<FamilyHistoryChangeRequestRecord['requestedArea']>('family_tree');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestCorrection, setRequestCorrection] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [loadedIntake, loadedChangeRequests] = await Promise.all([
          getFamilyHistoryIntake(currentUser.uid, { email: currentUser.email, displayName: currentUser.displayName }),
          getFamilyHistoryChangeRequests(currentUser.uid),
        ]);
        setIntake(loadedIntake);
        setChangeRequests(loadedChangeRequests);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const editable = intake ? canPatientEditIntake(intake.status) : false;
  const selfLabel = intake?.personalInfo.displayName || currentUser?.displayName || 'You';
  const readiness = useMemo<IntakeReadinessVerdict | null>(
    () => (intake ? computeIntakeReadiness(syncRelativeConditionsFromAssignments(intake)) : null),
    [intake],
  );

  const currentIndex = useMemo(() => Math.max(0, SCREENS.findIndex((screen) => screen.id === screenId)), [screenId]);
  const currentScreen = SCREENS[currentIndex];
  const progress = (currentIndex + 1) / SCREENS.length;

  /* --------- Autosave: debounced on edits, flush on navigation --------- */
  useEffect(() => {
    if (!intake || !editable) return;
    dirtyRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      void flushSave();
    }, 3500);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intake]);

  const flushSave = async () => {
    if (!intake || !dirtyRef.current) return;
    dirtyRef.current = false;
    setSaveState('saving');
    try {
      await saveFamilyHistoryIntakeDraft(syncRelativeConditionsFromAssignments(intake));
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch {
      setSaveState('idle');
    }
  };

  if (loading || overviewLoading || !intake || !readiness) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const setPersonalInfo = (patch: Partial<PatientFamilyHistoryIntakeRecord['personalInfo']>) => {
    setIntake((current) => {
      if (!current) return current;
      return {
        ...current,
        personalInfo: { ...current.personalInfo, ...patch, address: { ...current.personalInfo.address, ...(patch.address || {}) } },
      };
    });
  };

  const setFamilyStructure = (patch: Partial<PatientFamilyHistoryIntakeRecord['familyStructure']>) => {
    setIntake((current) => (current ? { ...current, familyStructure: { ...current.familyStructure, ...patch } } : current));
  };

  const updateNested = <T extends object>(field: keyof PatientFamilyHistoryIntakeRecord, patch: T) => {
    setIntake((current) => {
      if (!current) return current;
      return { ...current, [field]: { ...(current[field] as object), ...patch } };
    });
  };

  /** Patch a TOP-LEVEL field. updateNested merges into an object field; surgeries is an array. */
  const updateIntake = (patch: Partial<PatientFamilyHistoryIntakeRecord>) => {
    setIntake((current) => (current ? { ...current, ...patch } : current));
  };

  const personalHistory = ensurePersonalHistory(intake);
  const updatePersonalHistory = (patch: Partial<IntakePersonalHistory>) => {
    setIntake((current) => {
      if (!current) return current;
      const base = ensurePersonalHistory(current);
      return { ...current, personalHistory: { ...base, ...patch } };
    });
  };

  /**
   * Why `Continue →` is refusing, or null.
   *
   * The ONLY screen that gates. Everywhere else "I don't know" is a legitimate
   * answer and a member must never be trapped — but a history with no parents
   * in it cannot be read, and the form used to let a member walk straight past
   * that and then tell them they were 60% done on the dashboard.
   *
   * The escape hatch on the screen itself satisfies this: it records a parent
   * whose history is unknown, which is an answer.
   */
  const blockedReason = (() => {
    if (!editable || screenId !== 'family-tree' || !intake) return null;
    const missing = (['mother', 'father'] as IntakeRelation[])
      .filter((relation) => !intake.relatives.some((relative) => relative.relation === relation));
    if (missing.length === 0) return null;
    return `Add your ${missing.join(' and ')} to continue — or say you do not know their history.`;
  })();

  const goNext = async () => {
    if (blockedReason) return;
    await flushSave();
    const next = SCREENS[currentIndex + 1];
    if (next) setScreenId(next.id);
  };

  const goBack = async () => {
    await flushSave();
    if (currentIndex === 0) {
      setScreenId('overview');
      return;
    }
    const prev = SCREENS[currentIndex - 1];
    if (prev) setScreenId(prev.id);
  };

  const goTo = async (id: ScreenId) => {
    await flushSave();
    setScreenId(id);
  };

  const handleSubmit = async () => {
    if (!intake) return;
    setSubmitting(true);
    try {
      const next = syncRelativeConditionsFromAssignments(intake);
      await submitFamilyHistoryIntake(next);
      setIntake({ ...next, status: 'submitted', currentVersion: (next.currentVersion || 1) + 1 });
    } finally {
      setSubmitting(false);
    }
  };

  const submitChangeRequest = async () => {
    if (!currentUser || !intake || !requestMessage.trim()) return;
    setRequestSubmitting(true);
    try {
      await createFamilyHistoryChangeRequest({
        userId: currentUser.uid,
        intakeId: currentUser.uid,
        familyTreeId: intake.publishedFamilyTreeId || null,
        requestedArea: requestArea,
        message: requestMessage.trim(),
        proposedCorrection: requestCorrection.trim(),
      });
      const nextRequests = await getFamilyHistoryChangeRequests(currentUser.uid);
      setChangeRequests(nextRequests);
      setRequestMessage('');
      setRequestCorrection('');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const isLast = currentIndex === SCREENS.length - 1;
  const isFirst = currentIndex === 0;

  const renderScreen = () => {
    switch (screenId) {
      case 'welcome':
        return <WelcomeScreen displayName={selfLabel} />;
      case 'basics-name':
        return <NameScreen intake={intake} editable={editable} onPatch={setPersonalInfo} />;
      case 'basics-dob-sex':
        return <DobSexScreen intake={intake} editable={editable} onPatch={setPersonalInfo} />;
      case 'contact':
        return <ContactScreen intake={intake} editable={editable} onPatch={setPersonalInfo} />;
      case 'medications':
        return <MedicationsScreen editable={editable} personalHistory={personalHistory} updatePersonalHistory={updatePersonalHistory} />;
      case 'allergies':
        return <AllergiesScreen editable={editable} personalHistory={personalHistory} updatePersonalHistory={updatePersonalHistory} />;
      case 'diagnoses':
        return <DiagnosesScreen editable={editable} personalHistory={personalHistory} updatePersonalHistory={updatePersonalHistory} />;
      case 'surgeries':
        return <SurgeriesScreen editable={editable} surgeries={intake.surgeries || []} updateIntake={updateIntake} />;
      case 'genetic-tests':
        return <GeneticTestsScreen editable={editable} personalHistory={personalHistory} updatePersonalHistory={updatePersonalHistory} />;
      case 'ancestry':
        return <AncestryScreen editable={editable} personalHistory={personalHistory} updatePersonalHistory={updatePersonalHistory} />;
      case 'family-structure':
        return <FamilyStructureScreen intake={intake} editable={editable} onPatch={setFamilyStructure} />;
      case 'family-tree':
        return <FamilyTreeScreen intake={intake} editable={editable} setIntake={setIntake} />;
      case 'conditions-heart':
        return <ConditionsScreen group="heart" intake={intake} editable={editable} setIntake={setIntake} />;
      case 'conditions-cancer':
        return <ConditionsScreen group="cancer" intake={intake} editable={editable} setIntake={setIntake} />;
      case 'conditions-other':
        return <ConditionsScreen group="other" intake={intake} editable={editable} setIntake={setIntake} />;
      case 'lifestyle':
        return <LifestyleScreen intake={intake} editable={editable} updateNested={updateNested} />;
      case 'review':
        return (
          <ReviewScreen
            intake={intake}
            readiness={readiness}
            editable={editable}
            submitting={submitting}
            onSubmit={handleSubmit}
            onJumpTo={(id) => void goTo(id)}
            onPatientNotes={(next) => setIntake((current) => (current ? { ...current, patientNotes: next } : current))}
          />
        );
      default:
        return null;
    }
  };

  const isReleased = intake.status === 'released' || intake.status === 'signed_off';

  if (screenId === 'overview') {
    const overallPct = Math.round(readiness.completionRatio * 100);
    const relativesCount = (intake.relatives || []).length;
    const familyHistoryReady = relativesCount > 0 || Boolean(intake.familyStructure?.siblingsCount !== null && intake.familyStructure?.siblingsCount !== undefined);
    const medsCount = personalHistory.medications?.length || 0;
    const diagnosesCount = personalHistory.activeDiagnoses?.length || 0;
    const ancestriesCount = personalHistory.ancestries?.length || 0;
    const lifestyleKnown = Boolean(
      intake.lifestyle && Object.values(intake.lifestyle as unknown as Record<string, unknown>).some((v) => Boolean(v)),
    );

    return (
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-10 melio-stagger">
        <PageHeader
          breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'History' }]}
          eyebrow="Your health history"
          title="History & family tree"
          subtitle="Everything a clinician needs before your next review. Jump into any section, and you can come back at any time — we save as you go."
          rightSlot={
            <div
              className="rounded-full border px-4 py-2 text-xs"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-ink)' }}
            >
              <span style={{ color: 'var(--melio-text-muted)' }}>Overall </span>
              <span style={{ fontFamily: "var(--melio-display)", fontSize: 14 }}>{overallPct}%</span>
            </div>
          }
        />

        {!editable && <IntakeLockedNotice status={intake.status} />}
        {editable && intake.status === 'changes_requested' && (
          <ChangesRequestedNotice message={intake.opsClarificationMessage} />
        )}

        <div
          className="rounded-3xl border p-5 md:p-6 mb-6"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        >
          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: 'var(--melio-olive-deep)' }}>
                Overall progress
              </p>
              <p
                className="text-2xl font-normal"
                style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
              >
                {overallPct}% complete · {readiness.requiredPassed} of {readiness.requiredTotal} required items
              </p>
            </div>
            {editable && (
              <button
                type="button"
                onClick={() => void goTo(overallPct === 0 ? 'basics-name' : 'family-structure')}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
                style={{ backgroundColor: 'var(--melio-ink)', color: 'var(--melio-cream-warm)' }}
              >
                {overallPct === 0 ? 'Start history' : overallPct === 100 ? 'Review everything' : 'Continue where you left off'}
                <ChevronRightIcon size={14} />
              </button>
            )}
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{ backgroundColor: 'rgba(79, 93, 24, 0.14)' }}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${overallPct}%`, backgroundColor: 'var(--melio-olive)' }}
            />
          </div>
        </div>

        <TileGrid>
          <HistorySectionTile
            icon={<PersonIcon size={22} />}
            accent="terracotta"
            title="Personal"
            stat={
              diagnosesCount + medsCount > 0
                ? `${diagnosesCount} diagnoses · ${medsCount} medications`
                : 'Basics, medications, diagnoses'
            }
            onClick={() => void goTo('basics-name')}
          />
          <HistorySectionTile
            icon={<HistoryIcon size={22} />}
            accent="olive"
            title="Family"
            stat={
              familyHistoryReady
                ? `${relativesCount || '—'} relatives on file`
                : 'Parents, siblings, grandparents'
            }
            onClick={() => void goTo('family-structure')}
          />
          <HistorySectionTile
            icon={<PulseIcon size={22} />}
            accent="sea"
            title="Lifestyle"
            stat={lifestyleKnown ? 'Sleep, activity, stress captured' : 'Sleep, activity, stress'}
            onClick={() => void goTo('lifestyle')}
          />
          <HistorySectionTile
            icon={<BodyIcon size={22} />}
            accent="sun"
            title="Ancestry"
            stat={ancestriesCount > 0 ? `${ancestriesCount} region${ancestriesCount === 1 ? '' : 's'} selected` : 'Where your family comes from'}
            onClick={() => void goTo('ancestry')}
          />
        </TileGrid>

        <SectionCard
          eyebrow="Family tree"
          title="At a glance"
          rightSlot={
            editable ? (
              <button
                type="button"
                onClick={() => void goTo('family-tree')}
                className="text-xs underline"
                style={{ color: 'var(--melio-olive-deep)' }}
              >
                Edit tree →
              </button>
            ) : null
          }
          className="mt-6"
        >
          <FamilyTreeCanvas
            tree={publishedFamilyTree || buildFamilyTreePreviewFromIntake(intake)}
            melioNotesUnlocked={publishedFamilyTree?.status === 'published'}
            canEditPersonalNotes={editable}
            onPersonalNotesChange={(nodeId, notes) => {
              setIntake((current) => {
                if (!current) return current;
                if (nodeId === 'member-self') {
                  return { ...current, patientNotes: notes };
                }
                return {
                  ...current,
                  relatives: current.relatives.map((relative) =>
                    relative.id === nodeId ? { ...relative, notes } : relative,
                  ),
                };
              });
            }}
            selfLabel={selfLabel}
          />
        </SectionCard>

        {isLast && isReleased && (
          <div className="flex flex-wrap gap-2 mt-6">
            <ActionChip label="Request a correction" onClick={() => setTreeOpen(true)} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <HeaderBar
        status={intake.status}
        progress={progress}
        categoryLabel={currentScreen?.categoryLabel || ''}
        currentIndex={currentIndex}
        total={SCREENS.length}
        onShowTree={() => setTreeOpen(true)}
        onShowHelp={() => setHelpOpen(true)}
        saveState={saveState}
      />

      {!editable && <IntakeLockedNotice status={intake.status} showBackToHistory />}
      {editable && intake.status === 'changes_requested' && (
        <ChangesRequestedNotice message={intake.opsClarificationMessage} />
      )}

      {renderScreen()}

      {blockedReason && (
        <p className="text-sm mt-4" style={{ color: 'var(--melio-text-muted)' }}>
          {blockedReason}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
        <SecondaryButton onClick={() => void goBack()} disabled={isFirst}>
          ← Back
        </SecondaryButton>
        <div className="flex items-center gap-3">
          {currentScreen?.showSkip && !isLast && editable && (
            <GhostButton onClick={() => void goNext()}>Skip</GhostButton>
          )}
          {!isLast && (
            <PrimaryButton onClick={() => void goNext()} disabled={blockedReason !== null}>
              Continue →
            </PrimaryButton>
          )}
          {/* Forward motion out of the flow. On the last screen this slot was
              empty, so the member who had just finished had "← Back" as their
              only button — every affordance on the page pointed backwards. */}
          {isLast && !editable && (
            <Link
              to="/home"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
              style={{ backgroundColor: 'var(--melio-ink)', color: 'var(--melio-cream-warm)' }}
            >
              Back to your dashboard
              <ChevronRightIcon size={14} />
            </Link>
          )}
        </div>
      </div>

      {isLast && isReleased && (
        <CorrectionRequestCard
          intake={intake}
          changeRequests={changeRequests}
          requestArea={requestArea}
          requestMessage={requestMessage}
          requestCorrection={requestCorrection}
          requestSubmitting={requestSubmitting}
          onAreaChange={setRequestArea}
          onMessageChange={setRequestMessage}
          onCorrectionChange={setRequestCorrection}
          onSubmit={submitChangeRequest}
        />
      )}

      <div className="mt-8 flex flex-wrap gap-4 items-center justify-center text-sm" style={{ color: 'var(--melio-text-muted)' }}>
        <Link to="/home" className="underline" style={{ color: 'var(--melio-ink)' }}>
          Your dashboard
        </Link>
        <Link to="/history" className="underline" style={{ color: 'var(--melio-ink)' }}>
          All sections
        </Link>
        <Link to="/book/clinical-history" className="underline" style={{ color: 'var(--melio-text-muted)' }}>
          Book clinician review
        </Link>
      </div>

      {treeOpen && (
        <TreeDrawer
          intake={intake}
          publishedFamilyTree={publishedFamilyTree}
          selfLabel={selfLabel}
          onClose={() => setTreeOpen(false)}
        />
      )}
      {helpOpen && <HelpPopover onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function CorrectionRequestCard({
  intake,
  changeRequests,
  requestArea,
  requestMessage,
  requestCorrection,
  requestSubmitting,
  onAreaChange,
  onMessageChange,
  onCorrectionChange,
  onSubmit,
}: {
  intake: PatientFamilyHistoryIntakeRecord;
  changeRequests: FamilyHistoryChangeRequestRecord[];
  requestArea: FamilyHistoryChangeRequestRecord['requestedArea'];
  requestMessage: string;
  requestCorrection: string;
  requestSubmitting: boolean;
  onAreaChange: (area: FamilyHistoryChangeRequestRecord['requestedArea']) => void;
  onMessageChange: (value: string) => void;
  onCorrectionChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <div
      className="rounded-3xl border p-6 md:p-8 mt-6"
      style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
    >
      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--melio-sage)' }}>
        Post-sign-off changes
      </p>
      <h3 className="text-xl mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
        Request a correction
      </h3>
      <p className="text-sm mb-4" style={{ color: 'var(--melio-text-muted)' }}>
        Your intake is locked so Melio and your clinician are working from a stable record.
        If something needs changing, tell us here and we&rsquo;ll review it before implementing.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <FieldLabel>What needs changing?</FieldLabel>
          <Select value={requestArea} onChange={(event) => onAreaChange(event.target.value as FamilyHistoryChangeRequestRecord['requestedArea'])}>
            <option value="family_tree">Family tree structure</option>
            <option value="condition_history">Condition history</option>
            <option value="personal_history">Personal history</option>
            <option value="other">Other</option>
          </Select>
        </div>
        <div />
        <div className="md:col-span-2">
          <FieldLabel>Message to Melio</FieldLabel>
          <TextArea rows={3} value={requestMessage} onChange={(event) => onMessageChange(event.target.value)} />
        </div>
        <div className="md:col-span-2">
          <FieldLabel>Suggested correction</FieldLabel>
          <TextArea rows={2} value={requestCorrection} onChange={(event) => onCorrectionChange(event.target.value)} />
        </div>
      </div>
      <div className="mt-4">
        <PrimaryButton onClick={onSubmit} disabled={requestSubmitting || !requestMessage.trim()}>
          {requestSubmitting ? 'Sending…' : 'Submit correction request'}
        </PrimaryButton>
      </div>

      {changeRequests.length > 0 && (
        <div className="mt-6 space-y-3">
          {changeRequests.map((request) => (
            <div key={request.id} className="rounded-2xl border p-4" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="font-medium" style={{ color: 'var(--melio-charcoal)' }}>{String(request.requestedArea).replace(/_/g, ' ')}</p>
                <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--melio-text-muted)' }}>{request.status}</span>
              </div>
              <p className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>{request.message}</p>
              {request.resolutionNotes && (
                <p className="text-sm" style={{ color: 'var(--melio-charcoal)' }}>Resolution: {request.resolutionNotes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistorySectionTile({
  icon,
  title,
  stat,
  onClick,
  accent,
}: {
  icon: React.ReactNode;
  title: string;
  stat: string;
  onClick: () => void;
  accent: 'olive' | 'sea' | 'terracotta' | 'sun';
}) {
  const bg: Record<typeof accent, string> = {
    olive: 'rgba(79, 93, 24, 0.14)',
    sea: 'rgba(168, 196, 211, 0.28)',
    terracotta: 'rgba(228, 183, 156, 0.35)',
    sun: 'rgba(94, 66, 24, 0.22)',
  };
  const fg: Record<typeof accent, string> = {
    olive: 'var(--melio-olive-deep)',
    sea: 'var(--melio-sea-deep)',
    terracotta: 'var(--melio-terracotta-deep)',
    sun: '#8A6A24',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="melio-lift rounded-2xl border p-5 flex items-center gap-4 bg-white text-left"
      style={{ borderColor: 'var(--melio-border)' }}
    >
      <div
        aria-hidden
        className="shrink-0 rounded-2xl flex items-center justify-center"
        style={{ width: 48, height: 48, backgroundColor: bg[accent], color: fg[accent] }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p
          className="text-lg font-normal leading-tight"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          {title}
        </p>
        <p className="text-xs mt-1 truncate" style={{ color: 'var(--melio-text-muted)' }}>{stat}</p>
      </div>
      <ChevronRightIcon size={16} className="shrink-0" />
    </button>
  );
}
